import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLines, Reveal } from "./Reveal";
import { CtaButton } from "./CtaButton";
import { Photo } from "./Photo";
import { IMAGES } from "@/constants/images";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pb-20 pt-32 sm:pt-36">
      <div
        className="pointer-events-none absolute left-1/2 top-24 h-[60vh] w-[60vh] -translate-x-1/2 rounded-full opacity-[0.16] blur-3xl"
        style={{ background: "radial-gradient(circle, #d4af37, transparent 65%)" }}
      />

      <div className="mx-auto flex max-w-4xl flex-col items-center px-6 text-center sm:px-8">
        <Reveal delay={0.9}>
          <span className="mb-8 inline-flex max-w-full flex-wrap items-center justify-center gap-3 font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#d4af37] sm:text-[11px] sm:tracking-[0.3em]">
            <span className="hidden h-px w-8 bg-[#d4af37] sm:block" /> For 6-Figure Traders Who Hit The Wall
            <span className="hidden h-px w-8 bg-[#d4af37] sm:block" />
          </span>
        </Reveal>

        <h1 className="font-['Playfair_Display'] text-4xl font-semibold leading-[1.03] tracking-tight sm:text-6xl sm:leading-[1.0] lg:text-7xl">
          <MaskLines delay={0.35} className="block text-[#f5f5f0]" lines={["The 6 Figure Stock Trading"]} />
          <MaskLines delay={0.5} className="block" lineClass="text-white-gold" lines={["Coach For Traders Ready To Break Through."]} />
        </h1>

        <Reveal delay={1.05} className="mt-8 max-w-2xl">
          <p className="text-base leading-relaxed text-[#a1a1aa] sm:text-lg">
            You already make six figures. You&rsquo;ve also felt the ceiling &mdash; the same account
            size, the same self-sabotage, the same plateau. Shabz Fazl is the private, paid-only{" "}
            <strong className="text-[#f5f5f0]">6 figure stock trading coach</strong> who helps elite
            traders trade bigger, cleaner and calmer &mdash; and finally live the life their P&amp;L
            keeps promising.
          </p>
        </Reveal>

        <Reveal delay={1.2} className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <CtaButton testid="hero-cta" />
          <a
            href="#life"
            data-testid="hero-secondary"
            className="font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.24em] text-[#a1a1aa] underline-offset-8 transition-colors hover:text-[#d4af37] hover:underline"
          >
            See the lifestyle &rarr;
          </a>
        </Reveal>

        {/* Centered framed portrait */}
        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="corner-frame relative mt-16 w-full max-w-md"
        >
          <div className="absolute -inset-3 border border-[#d4af37]/40" />
          <div className="absolute -inset-1.5 border border-[#d4af37]/15" />
          <Photo
            testid="hero-portrait"
            src={IMAGES.hero}
            alt="Shabz Fazl, 6 figure stock trading coach"
            label="Hero portrait of Shabz"
            className="aspect-[4/5] w-full grayscale-[0.1]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#f5f5f0]">
            Shabz Fazl &mdash; @shabzfazl
          </div>
        </motion.div>
      </div>
    </section>
  );
};
